import type { RoleInfo, UserInfoResponse } from '@api/types/auth';

export type AppRoleCode = 'ADMIN' | 'EXPERT' | 'APPLICANT';

export interface StoredUserInfo {
  id?: string;
  name?: string;
  role?: string;
  roleCode?: AppRoleCode;
  currentRoleId?: string;
  currentRole?: RoleInfo;
  roleList?: RoleInfo[];
  passwordReset?: boolean;
}

export function normalizeRoleCode(roleCode?: string | null): AppRoleCode | null {
  const normalizedRoleCode = roleCode?.toUpperCase();
  if (
    normalizedRoleCode === 'ADMIN'
    || normalizedRoleCode === 'EXPERT'
    || normalizedRoleCode === 'APPLICANT'
  ) {
    return normalizedRoleCode;
  }
  return null;
}

export function getDefaultPathByRole(roleCode?: string | null): string {
  switch (normalizeRoleCode(roleCode)) {
    case 'ADMIN':
      return '/admin';
    case 'EXPERT':
      return '/expert';
    case 'APPLICANT':
      return '/dashboard';
    default:
      return '/';
  }
}

function decodeBase64Url(payload: string): string {
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  return window.atob(paddedBase64);
}

export function getUserIdFromAccessToken(accessToken?: string | null): string | undefined {
  if (!accessToken) {
    return undefined;
  }

  try {
    const payload = accessToken.split('.')[1];
    if (!payload) {
      return undefined;
    }

    const tokenPayload = JSON.parse(decodeBase64Url(payload));
    const jwtIdentity = typeof tokenPayload.sub === 'string' ? tokenPayload.sub : '';
    const identityPartList = jwtIdentity.split(':');
    if (identityPartList.length < 2) {
      return undefined;
    }
    return identityPartList[1] || undefined;
  } catch (error) {
    console.error('解析 access token 失败:', error);
    return undefined;
  }
}

export function buildStoredUserInfo(
  userInfo: UserInfoResponse,
  accessToken?: string | null,
): StoredUserInfo {
  const roleCode = normalizeRoleCode(userInfo.currentRole?.code);

  return {
    id: getUserIdFromAccessToken(accessToken),
    name: userInfo.name,
    role: roleCode ? roleCode.toLowerCase() : undefined,
    roleCode: roleCode || undefined,
    currentRoleId: userInfo.currentRoleId,
    currentRole: userInfo.currentRole,
    roleList: userInfo.roleList || [],
    passwordReset: userInfo.passwordReset,
  };
}

export function normalizeStoredUserInfo(
  userInfo: StoredUserInfo | null,
  accessToken?: string | null,
): StoredUserInfo | null {
  if (!userInfo) {
    return null;
  }

  const roleCode = normalizeRoleCode(
    userInfo.roleCode || userInfo.currentRole?.code || userInfo.role,
  );

  return {
    ...userInfo,
    id: userInfo.id || getUserIdFromAccessToken(accessToken),
    role: roleCode ? roleCode.toLowerCase() : userInfo.role,
    roleCode: roleCode || undefined,
  };
}

export function readStoredUserInfo(): StoredUserInfo | null {
  const userText = sessionStorage.getItem('user');
  if (!userText) {
    return null;
  }

  try {
    return JSON.parse(userText) as StoredUserInfo;
  } catch (error) {
    console.error('解析用户缓存失败:', error);
    return null;
  }
}

export function saveStoredUserInfo(
  userInfo: UserInfoResponse,
  accessToken?: string | null,
): StoredUserInfo {
  const storedUserInfo = buildStoredUserInfo(userInfo, accessToken);
  sessionStorage.setItem('user', JSON.stringify(storedUserInfo));
  return storedUserInfo;
}

export function persistNormalizedStoredUserInfo(
  userInfo: StoredUserInfo | null,
  accessToken?: string | null,
): StoredUserInfo | null {
  const normalizedStoredUserInfo = normalizeStoredUserInfo(userInfo, accessToken);
  if (normalizedStoredUserInfo) {
    sessionStorage.setItem('user', JSON.stringify(normalizedStoredUserInfo));
  }
  return normalizedStoredUserInfo;
}

